import { query, queryAll } from './utils'

class AnimationError extends Error {
  constructor(componentName) {
    super(`Could not load [${componentName}]`)
    this.name = 'AnimationError'
  }
}

export const homepageAnimation = () => {
  const img = query('.homepage__media__container > img')
  const video = query('.homepage__media__container > video')

  if (!img && !video) return

  if (img) {
    // wait for the image to load before starting animation
    const image = new Image()
    image.addEventListener('load', () => {
      img.classList.add('loaded')
    })
    image.src = img.src
  } else {
    video.controls = false
    video.autoplay = true
    video.muted = true
    video.loop = true
    video.playsInline = true
    video.load()
    video.addEventListener('loadeddata', () => {
      video.classList.add('loaded')
    })
  }
}

// gradually load menu items on expand
export const menuAnimation = (show) => {
  const menuContainer = query('.menu__wrapper')
  if (!menuContainer) {
    throw AnimationError('Menu')
  }

  const menuTexts = queryAll('.menu__item-top-row > a')
  if (menuTexts.length == 0) {
    throw AnimationError('Menu items')
  }

  staggeredAnimation(menuTexts, 100, show)
}

export const heroAnimation = () => {
  const containers = queryAll('.hero')
  containers.forEach((c) =>
    componentAnimation({
      container: c,
      triggerClass: 'loaded',
      elementSelectors: ['.hero__image', '.hero__header', '.hero__body', '.hero__label'],
    })
  )
}

// expand filters on mobile
export const filtersExpandAnimation = () => {
  const animate = () => {
    const toggleELement = query('.exposed-filters__toggle')
    if (!toggleELement) {
      return
    }
    const commonParent = query('.exposed-filters')
    const optionContainer = query('.exposed-filters--second-part')
    const options = queryAll('.filter-option', optionContainer)

    // use onclick instead of adding listener to prevent double event addition
    toggleELement.onclick = () => {
      commonParent.classList.toggle('expanded')
      optionContainer.style.maxHeight = commonParent.classList.contains('expanded')
        ? optionContainer.scrollHeight + 'px'
        : null
    }

    staggeredAnimation(options, 100, 100)
  }

  // observer changes in body to detect filtering and re-rendering of node
  const observer = new MutationObserver(animate)
  observer.observe(document.body, {
    childList: true,
    subtree: true,
  })

  animate()
}

export const employeeClientAnimation = () => {
  const animate = () => {
    const employees = queryAll('.employee')
    const clients = queryAll('.client')
    const container = query('.list__grid')

    container?.classList.add('loaded')
    staggeredAnimation(employees, 100)
    staggeredAnimation(clients, 100)
  }

  // observer changes in body to detect filtering and re-rendering of node
  const observer = new MutationObserver(animate)
  observer.observe(document.body, {
    childList: true,
    subtree: true,
  })

  animate()
}

// animate every advice component on page
export const adviceAnimation = () => {
  const paragraphs = queryAll('.advice')
  if (paragraphs.length === 0) {
    return
  }

  // observe each advice paragraph to fade it in on scroll
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          componentAnimation(
            {
              container: entry.target,
              triggerClass: 'loaded',
              elementSelectors: ['.advice__number', '.advice__quote', '.advice__label', '.advice__author'],
            },
            300,
            100
          )
        }
      })
    },
    { threshold: 0.4 }
  )

  paragraphs.forEach((p) => observer.observe(p))
}

export const bigHeaderAnimation = () => {
  queryAll('.big-header').forEach((header) => header.classList.add('loaded'))
}

function componentAnimation({ container, triggerClass, elementSelectors }, delay, baseDelay, show) {
  let selectParent = container // default parent for querySelector() function

  // if container is given as string selector (meaning there is only one per page)
  if (typeof container === 'string') {
    container = query(container) // set container to object from given selector
    selectParent = document // set default parent to document
  }

  container?.classList.add(triggerClass) // add class to trigger css transitions
  staggeredAnimation(
    elementSelectors.map((el) => query(el, selectParent)),
    delay,
    baseDelay,
    show
  )
}

// gradually load submenu items on expand
export const submenuAnimation = (parent, show) => {
  const submenuList = query('.submenu__items', parent)
  submenuList.style.maxHeight = show ? submenuList.scrollHeight + 'px' : null // set the height needed for list's items

  const submenuTexts = queryAll('.menu__item > a', submenuList)
  if (submenuTexts.length == 0) {
    throw AnimationError('Submenu items')
  }

  staggeredAnimation(submenuTexts, 100, show)
}

export function staggeredAnimation(elements, delay = 300, baseDelay = 0, show = true) {
  elements.forEach((el, i) => {
    el?.style.transitionDelay = show ? `${i * delay + baseDelay}ms` : null
  })
}

// delay css transition when mouse leaves the element
export function delayOnLeave(hoverElement, targetElement, delays) {
  if (typeof hoverElement !== 'object') {
    hoverElement = query(hoverElement)
  }

  if (typeof targetElement !== 'object') {
    targetElement = query(targetElement)
  }

  if (!targetElement || !hoverElement) {
    return
  }

  hoverElement.addEventListener('mouseover', () => {
    targetElement.style.transitionDelay = null
  })

  hoverElement.addEventListener('mouseout', () => {
    targetElement.style.transitionDelay = delays
  })
}
